import type { MutationOptions } from "@tanstack/react-query";
import { atom, type Getter } from "jotai";
import { atomWithMutation } from "jotai-tanstack-query";

import { err, ok, type Result, type ResultAsync } from "#/shared/lib/result";

export interface ResourceMutation<TData, TVariables, TError> {
  execute: (variables: TVariables) => Promise<Result<TData, TError>>;
  isPending: boolean;
}

interface ResultMutationDefinition<
  TData,
  TVariables,
  TError,
  TContext,
> extends Omit<
  MutationOptions<TData, unknown, TVariables, TContext>,
  "mutationFn"
> {
  mutationFn: (variables: TVariables) => ResultAsync<TData, TError>;
  normalizeUnexpectedFailure: (cause: unknown) => TError;
}

class ExpectedResourceMutationFailure<TError> extends Error {
  override readonly cause: TError;
  readonly failure: TError;

  constructor(failure: TError) {
    super("Resource mutation returned an expected failure");
    this.name = "ExpectedResourceMutationFailure";
    this.cause = failure;
    this.failure = failure;
  }
}

export function createResultMutationAtom<
  TData,
  TVariables,
  TError,
  TContext = unknown,
>(
  readDefinition: (
    get: Getter,
  ) => ResultMutationDefinition<TData, TVariables, TError, TContext>,
) {
  class ExpectedMutationFailure extends ExpectedResourceMutationFailure<TError> {}

  const definitionAtom = atom((get) => readDefinition(get));
  const rawMutationAtom = atomWithMutation<
    TData,
    TVariables,
    unknown,
    TContext
  >((get) => {
    const {
      mutationFn,
      normalizeUnexpectedFailure: _,
      ...options
    } = get(definitionAtom);

    return {
      ...options,
      async mutationFn(variables) {
        const result = await mutationFn(variables);

        return result.match(
          (value) => value,
          (failure) => {
            throw new ExpectedMutationFailure(failure);
          },
        );
      },
    };
  });

  return atom<ResourceMutation<TData, TVariables, TError>>((get) => {
    const definition = get(definitionAtom);
    const rawMutation = get(rawMutationAtom);

    return {
      async execute(variables) {
        try {
          return ok(await rawMutation.mutateAsync(variables));
        } catch (cause: unknown) {
          try {
            if (cause instanceof ExpectedMutationFailure) {
              return err(cause.failure);
            }
          } catch {
            // 原始 rejection 可能拒绝原型访问，继续交给领域 normalizer。
          }

          return err(definition.normalizeUnexpectedFailure(cause));
        }
      },
      isPending: rawMutation.isPending,
    };
  });
}
